/**
 * The models every Amoreg agent offers, with their reasoning and speed options.
 *
 * Each agent (Amoreg, Inspector) lists two models in the composer: its own
 * name on GPT-6.1 Sol, and "Pro" on Claude Opus 5.5. Beside the model, T3's
 * Codex-style traits menu sets the reasoning effort and the service tier:
 *
 * - Standard — the model's normal lane.
 * - Fast — the same model on the provider's priority lane, through the AI
 *   Gateway's `-fast` twin, at twice the token price. The gateway reports the
 *   cost, so usage needs no local rates.
 * - Ultrafast — OpenAI only, on models OpenAI serves at that tier, called
 *   directly (the gateway has no Ultrafast lane) at six times the token price.
 *   The composer asks for confirmation before switching it on.
 *
 * The server (composer options, gateway fallback, usage) and the agents
 * (per-turn model resolution) both read this table, so the two cannot drift.
 */
import { MODEL_TOKEN_PRICES, type UltrafastModelId } from "./modelPricing.ts";

export type AgentModelFamily = "standard" | "pro";
export type AgentServiceTier = "default" | "fast" | "ultrafast";

export interface AgentEffortOption {
  readonly id: string;
  readonly label: string;
  /**
   * How much a turn at this effort counts against usage, over its provider
   * cost (`agentRouteUsageWeight`). Absent means 1.
   */
  readonly usageWeight?: number;
  /** Retired efforts whose saved choices now run at this one. */
  readonly formerIds?: ReadonlyArray<string>;
}

export interface AgentModelFamilyDefinition {
  readonly family: AgentModelFamily;
  readonly gatewayModel: string;
  readonly provider: "openai" | "anthropic";
  /** Provider-native effort values, lowest to highest. */
  readonly efforts: ReadonlyArray<AgentEffortOption>;
  readonly defaultEffort: string;
  /** Tiers beyond Standard this model runs on. */
  readonly serviceTiers: ReadonlyArray<Exclude<AgentServiceTier, "default">>;
  /** OpenAI model id for direct Ultrafast calls. */
  readonly ultrafastModel?: UltrafastModelId;
  /**
   * How much every turn on this model counts against usage, over its provider
   * cost, before any effort weight (`agentRouteUsageWeight`). Absent means 1.
   */
  readonly usageWeight?: number;
  /** USD per 1M tokens as people are charged, usage weight included. */
  readonly tokenPricing: {
    readonly input: number;
    readonly cachedInput: number;
    readonly cacheWrite: number;
    readonly output: number;
  };
}

/**
 * On the standard model High counts double against usage: it runs at the
 * provider's xhigh, the deepest reasoning offered (Max is not), and its long
 * turns are priced to match. The menu does not show the weight.
 */
export const HIGH_USAGE_WEIGHT = 2;

/** Pro counts 20% over its provider cost at every effort, High included. */
export const PRO_USAGE_WEIGHT = 1.2;

/**
 * The effort ladder, lowest to highest. High is the provider's xhigh: the
 * provider's own high is not offered, and neither is Max.
 */
const PRO_EFFORTS: ReadonlyArray<AgentEffortOption> = [
  { id: "low", label: "Low" },
  { id: "medium", label: "Medium" },
  { id: "xhigh", label: "High", formerIds: ["high"] },
];

const STANDARD_EFFORTS: ReadonlyArray<AgentEffortOption> = PRO_EFFORTS.map((effort) =>
  effort.id === "xhigh" ? { ...effort, usageWeight: HIGH_USAGE_WEIGHT } : effort,
);

const RETIRED_EFFORTS: ReadonlyMap<unknown, string> = new Map(
  PRO_EFFORTS.flatMap((effort) => (effort.formerIds ?? []).map((former) => [former, effort.id] as const)),
);

/** A saved effort as it runs today: a retired one becomes its successor. */
export function currentAgentEffort<E>(effort: E): E | string {
  return RETIRED_EFFORTS.get(effort) ?? effort;
}

const weighted = (
  pricing: { input: number; cachedInput: number; cacheWrite: number; output: number },
  weight: number,
) => ({
  input: pricing.input * weight,
  cachedInput: pricing.cachedInput * weight,
  cacheWrite: pricing.cacheWrite * weight,
  output: pricing.output * weight,
});

export const AGENT_MODEL_FAMILIES: Readonly<Record<AgentModelFamily, AgentModelFamilyDefinition>> = {
  standard: {
    family: "standard",
    gatewayModel: "openai/gpt-6.1-sol",
    provider: "openai",
    efforts: STANDARD_EFFORTS,
    defaultEffort: "medium",
    serviceTiers: ["fast", "ultrafast"],
    // OpenAI does not serve GPT-6.1 Sol at Ultrafast yet (it rejects the
    // service tier), so Ultrafast turns run on GPT-6 Astra, where it is
    // generally available. Switch to "gpt-6.1-sol" once OpenAI opens it.
    ultrafastModel: "gpt-6-astra",
    tokenPricing: MODEL_TOKEN_PRICES.amoreg,
  },
  pro: {
    family: "pro",
    gatewayModel: "anthropic/claude-opus-5.5",
    provider: "anthropic",
    efforts: PRO_EFFORTS,
    // Pro's High carries no surcharge of its own, so it is the default.
    defaultEffort: "xhigh",
    serviceTiers: ["fast"],
    usageWeight: PRO_USAGE_WEIGHT,
    tokenPricing: weighted(MODEL_TOKEN_PRICES["amoreg-pro"], PRO_USAGE_WEIGHT),
  },
};

/** Composer option ids, the same ones T3's Codex provider uses. */
export const AGENT_EFFORT_OPTION_ID = "reasoningEffort";
export const AGENT_SERVICE_TIER_OPTION_ID = "serviceTier";

/** Per-turn request headers the server sends and every agent reads. */
export const AGENT_MODEL_HEADER = "x-amoreg-model";
export const AGENT_EFFORT_HEADER = "x-amoreg-effort";
export const AGENT_SPEED_HEADER = "x-amoreg-speed";

/** The model slug an agent lists for a family: `amoreg`, `amoreg-pro`. */
export function agentModelSlug(agentTier: string, family: AgentModelFamily): string {
  return family === "pro" ? `${agentTier}-pro` : agentTier;
}

/**
 * The family a model slug names. `-pro` slugs are Pro; everything else —
 * the agent's own slug, the retired `amoreg-flash`, an unknown or absent
 * slug — is the standard model.
 */
export function agentModelFamily(slug: string | null | undefined): AgentModelFamily {
  return typeof slug === "string" && slug.endsWith("-pro") ? "pro" : "standard";
}

export interface AgentModelRoute {
  readonly family: AgentModelFamily;
  readonly effort: string;
  readonly serviceTier: AgentServiceTier;
}

/** A route's effort and tier, checked against what its model supports. */
export function normalizeAgentModelRoute(
  family: AgentModelFamily,
  effort: unknown,
  serviceTier: unknown,
): AgentModelRoute {
  const definition = AGENT_MODEL_FAMILIES[family];
  const requested = currentAgentEffort(effort);
  return {
    family,
    effort: definition.efforts.some((option) => option.id === requested)
      ? (requested as string)
      : definition.defaultEffort,
    serviceTier:
      typeof serviceTier === "string" &&
      (definition.serviceTiers as readonly string[]).includes(serviceTier)
        ? (serviceTier as AgentServiceTier)
        : "default",
  };
}

/** A composer selection: the model slug plus its reasoning and tier options. */
export function agentModelRouteFromSelection(
  model: string | null | undefined,
  options: ReadonlyArray<{ readonly id: string; readonly value: string | boolean }> | undefined,
): AgentModelRoute {
  const read = (id: string) => options?.find((option) => option.id === id)?.value;
  return normalizeAgentModelRoute(
    agentModelFamily(model),
    read(AGENT_EFFORT_OPTION_ID),
    read(AGENT_SERVICE_TIER_OPTION_ID),
  );
}

/**
 * The route as one string, as the server carries it per thread and the usage
 * ledger records it: `amoreg@medium`, `amoreg-pro@xhigh+fast`.
 */
export function formatAgentModelRoute(agentTier: string, route: AgentModelRoute): string {
  const tier = route.serviceTier === "default" ? "" : `+${route.serviceTier}`;
  return `${agentModelSlug(agentTier, route.family)}@${route.effort}${tier}`;
}

/** Inverse of `formatAgentModelRoute`; a bare slug (a pre-picker tier) takes its defaults. */
export function parseAgentModelRoute(value: string | null | undefined): AgentModelRoute {
  const [slug, rest = ""] = (value ?? "").split("@", 2);
  const [effort, serviceTier] = rest.split("+", 2);
  return normalizeAgentModelRoute(agentModelFamily(slug), effort, serviceTier);
}

/** How much a route's turns count against usage, over their provider cost. */
export function agentRouteUsageWeight(route: AgentModelRoute): number {
  const definition = AGENT_MODEL_FAMILIES[route.family];
  const effortWeight =
    definition.efforts.find((effort) => effort.id === route.effort)?.usageWeight ?? 1;
  return (definition.usageWeight ?? 1) * effortWeight;
}

/**
 * The model id for a route: the model, its gateway `-fast` twin, or the OpenAI
 * model Ultrafast runs on (which need not be the family's own model).
 */
export function agentGatewayModel(route: AgentModelRoute): string {
  const { gatewayModel, ultrafastModel } = AGENT_MODEL_FAMILIES[route.family];
  if (route.serviceTier === "ultrafast" && ultrafastModel) return `openai/${ultrafastModel}`;
  return route.serviceTier === "fast" ? `${gatewayModel}-fast` : gatewayModel;
}

/**
 * Claude Opus 5.5 omits its thinking text unless asked; without this a Pro
 * turn streamed empty reasoning deltas and showed nothing for 20–35 s.
 */
export const ANTHROPIC_SUMMARIZED_THINKING = { type: "adaptive", display: "summarized" } as const;

/** AI SDK provider options pinning the route's effort (and Ultrafast), per provider. */
export function agentProviderOptions(
  route: AgentModelRoute,
): Record<string, Record<string, string | typeof ANTHROPIC_SUMMARIZED_THINKING>> {
  const definition = AGENT_MODEL_FAMILIES[route.family];
  if (definition.provider === "anthropic") {
    return { anthropic: { effort: route.effort, thinking: ANTHROPIC_SUMMARIZED_THINKING } };
  }
  return {
    openai: {
      reasoningEffort: route.effort,
      ...(route.serviceTier === "ultrafast" ? { serviceTier: "ultrafast" } : {}),
    },
  };
}

/** The route a turn's request asks for; a server without the effort headers sends only a tier. */
export function agentModelRouteFromHeaders(headers: Headers): AgentModelRoute {
  return normalizeAgentModelRoute(
    agentModelFamily(headers.get(AGENT_MODEL_HEADER)),
    headers.get(AGENT_EFFORT_HEADER),
    headers.get(AGENT_SPEED_HEADER),
  );
}
